import { HttpInterceptorFn, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Marks a request that must leave the app untouched — no Authorization header,
 * no withCredentials, no 401 refresh-and-retry.
 *
 * Only needed for third-party endpoints that carry their own credentials in the
 * URL, such as presigned S3 upload/download URLs: sending our Bearer token
 * alongside a SigV4 query signature makes S3 reject the request, and
 * withCredentials turns it into a preflight the bucket will not answer.
 */
export const SKIP_AUTH_HEADERS = new HttpContextToken<boolean>(() => false);

/**
 * Attaches the JWT access token to outgoing HTTP requests and
 * enables withCredentials for cookie-based auth (OAuth flow).
 * Prevents infinite logout loops by skipping auto-logout on logout calls.
 * Handles silent token refresh upon encountering 401 unauthorized errors.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Requests opting out are forwarded exactly as the caller built them.
  if (req.context.get(SKIP_AUTH_HEADERS)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getAccessToken();

  // Clone with credentials (for cookie-based OAuth auth)
  let cloned = req.clone({ withCredentials: true });

  // Attach Bearer token if available (for localStorage-based auth)
  if (token) {
    cloned = cloned.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  // Track special requests to prevent infinite loops and invalid refresh attempts
  const isLoginRequest = req.url.includes('/auth/login');
  const isRegisterRequest = req.url.includes('/auth/register');
  const isLogoutRequest = req.url.includes('/auth/logout');
  const isRefreshRequest = req.url.includes('/auth/refresh');

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get 401, and it's not login, register, logout, or refresh request, try to refresh
      if (error.status === 401 && !isLoginRequest && !isRegisterRequest && !isLogoutRequest && !isRefreshRequest) {
        return authService.refresh().pipe(
          switchMap((authResponse) => {
            // Token refresh succeeded! Retry the original request with the new token
            const newToken = authResponse.tokens?.accessToken;
            let retryReq = req.clone({ withCredentials: true });
            if (newToken) {
              retryReq = retryReq.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
            }
            return next(retryReq);
          }),
          catchError((refreshError) => {
            // If the user was authenticated, perform full logout (which redirects)
            // Otherwise, just clear the session without redirecting
            if (authService.isAuthenticated()) {
              authService.logout();
            } else {
              authService.clearSession();
            }
            return throwError(() => refreshError);
          })
        );
      }

      // If it's a 401 from a logout or refresh request, clear session to break any loop
      if (error.status === 401 && (isLogoutRequest || isRefreshRequest)) {
        authService.clearSession();
      }

      return throwError(() => error);
    })
  );
};
